import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { matchRecordRoute } from './record-routes'
import { PLATFORM_RECORD_TYPES } from './registry-record-types'

const Ctx = createContext(null)

// Pages whose URL can't name their record declare it (ruling 1). The last
// declaration mounted wins; unmounting removes it. Mounted around the page
// column (page/layout.js), so the breadcrumb bar's History sees what the
// page below it declares.
export const AuditRecordProvider = ({ children }) => {
  const [stack, setStack] = useState([])
  // stable for the provider's life, so declarations don't re-run
  const declarations = useMemo(() => ({
    push: rec => setStack(s => [...s, rec]),
    pop: rec => setStack(s => s.filter(x => x !== rec))
  }), [])
  const value = useMemo(() => ({ stack, declarations }), [stack, declarations])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

// useAuditRecord({ type, id, children, role, tree, platform, organizationID }):
// this page shows record type:id. children: its History includes its child
// records by default; tree: everything under it, with a "This level only"
// switch.
// role: { org: '<ROLE_ORG key>' }, the permission whose update right lets an
// editor read its History (core event.RecordScope); null = audit-log
// holders only. Callers may pass role as an inline object: it is compared
// by value.
// platform: the system audit log's alone (record-routes.js); by default
// whether the type is one of the registry's PLATFORM_RECORD_TYPES.
// organizationID: a staff page's record of any organization, as the page
// loaded it (null: it belongs to none). History then shows for a reader
// without SYS_AUDIT_LOG only when it is their current organization's
// (record-routes.js `recordOrg`). Declare it once the record is loaded: pass
// no id until then.
export const useAuditRecord = ({ type, id, children = false, role = null, tree = false, platform, organizationID }) => {
  const ctx = useContext(Ctx)
  const declarations = ctx && ctx.declarations
  const roleKey = role ? JSON.stringify(role) : ''
  const isPlatform = platform === undefined ? PLATFORM_RECORD_TYPES.includes(type) : Boolean(platform)
  useEffect(() => {
    if (!declarations || !type || id === undefined || id === null || id === '' || Number.isNaN(id)) return undefined
    const rec = { type, id: String(id), children: Boolean(children), role: roleKey ? JSON.parse(roleKey) : null }
    if (tree) rec.tree = true
    if (isPlatform) rec.platform = true
    if (organizationID !== undefined) {
      rec.recordOrg = 'page'
      rec.organizationID = organizationID === null ? null : Number(organizationID)
    }
    declarations.push(rec)
    return () => declarations.pop(rec)
  }, [declarations, type, id, children, tree, roleKey, isPlatform, organizationID])
}

// The record the current page shows: the page's own declaration, else the
// URL's (RECORD_ROUTES, ROOT_ROUTES), else null. { type, id, children,
// tree, role, platform, recordOrg, organizationID }, and for a section's top
// its root and title. organizationID is undefined while nobody has said it.
export const useCurrentAuditRecord = () => {
  const ctx = useContext(Ctx)
  const { pathname } = useLocation()
  const declared = ctx && ctx.stack.length ? ctx.stack[ctx.stack.length - 1] : null
  const matched = useMemo(() => matchRecordRoute(pathname), [pathname])
  return useMemo(() => {
    const recordOrg = matched && !matched.root ? matched.entry.recordOrg : null
    if (declared) {
      // a declaration of the URL's own staff record that doesn't say its
      // organization stays a record of any organization, its organization
      // unknown
      const same = matched && matched.type === declared.type && matched.id === declared.id
      return same && recordOrg && !declared.recordOrg ? { ...declared, recordOrg: recordOrg === 'id' ? 'id' : 'unknown', organizationID: recordOrg === 'id' ? Number(matched.id) : undefined } : declared
    }
    if (!matched) return null
    const rec = { type: matched.type, id: matched.id, children: matched.children, role: matched.entry.role }
    if (matched.entry.platform) rec.platform = true
    if (recordOrg) {
      rec.recordOrg = recordOrg
      if (recordOrg === 'id') rec.organizationID = Number(matched.id)
    }
    if (matched.tree) rec.tree = true
    return matched.root ? { ...rec, root: matched.root, title: matched.title } : rec
  }, [declared, matched])
}
